import { deepEqual, keys, values } from '@direct-frontend/stdlib';

import { BaseValidationError, ValidationErrorSource, ValidationState, ValidatorType } from '../../types';
import { FormSliceState } from '../../store/types';

/**
 * Удаляет ошибки полей, источник которых совпадает с переданным
 * @param fieldsState Стейт полей формы
 * @param fieldsValidationErrorsMap Объект с ошибками полей формы
 * @param source Источник, ошибки из которого нужно удалить
 * @param validatorTypes Типы валидаторов для которых нужно удалить ошибки
 * @returns Объект ошибок после удаления
 */
export function cleanupValidationErrorsBySource(
    fieldsState: FormSliceState['forms'][string]['fieldsState'],
    fieldsValidation: ValidationState<BaseValidationError>['fieldsValidation'],
    source: ValidationErrorSource,
    validatorTypes: ValidatorType[]
) {
    // Если источником является форма
    if (source.type === 'form') {
        // То проходимся по всем полям формы
        for (const fieldKey of keys(fieldsValidation)) {
            const validationErrorsMap = fieldsValidation[fieldKey]?.errorsMap;
            if (!validationErrorsMap) {
                continue;
            }

            // И удаляем ошибки источником которых является форма
            for (const validatorType of validatorTypes) {
                validationErrorsMap[validatorType] = validationErrorsMap[validatorType].filter(
                    validationError => !deepEqual(validationError.meta.source, source)
                );
            }
        }

        return fieldsValidation;
    }

    // Если источник поле
    // то сбрасываем все ошибки поля-источника
    const validationErrorsMap = fieldsValidation[source.fieldKey]?.errorsMap;
    if (validationErrorsMap) {
        for (const validatorType of validatorTypes) {
            validationErrorsMap[validatorType] = [];
        }
    }

    // И проходимся по дереву вниз и так же очищаем ошибки поля-источника
    function cleanupFieldAndChildren(fieldId: string) {
        const fieldState = fieldsState[fieldId];
        const validationErrorsMap = fieldsValidation[fieldId]?.errorsMap;

        if (!fieldState || !validationErrorsMap) return;

        // В дочерних поля оставляем только те ошибки, у которых отличается source
        for (const validatorType of validatorTypes) {
            validationErrorsMap[validatorType] = validationErrorsMap[validatorType].filter(
                validationError => !deepEqual(validationError.meta.source, source)
            );
        }

        if ('fields' in fieldState) {
            for (const nestedFieldId of values(fieldState.fields)) {
                cleanupFieldAndChildren(nestedFieldId);
            }
        }
        if ('order' in fieldState) {
            for (const arrayItemId of fieldState.order) {
                cleanupFieldAndChildren(arrayItemId);
            }
        }
    }

    cleanupFieldAndChildren(source.fieldKey);

    return fieldsValidation;
}
